import { useTranslation } from 'react-i18next';
import { useState, useCallback, useEffect } from 'react';
import { Layers, Clock, ShieldCheck, ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { def1, def2, def3, def4, def5, def6, def7, def8, def9 } from '../../assets/images';

const getReasons = (t) => [
  {
    id: 1,
    title: t('whyChooseUs.reason1.title'),
    description: t('whyChooseUs.reason1.desc'),
    points: [
      t('whyChooseUs.reason1.p1'),
      t('whyChooseUs.reason1.p2')
    ],
    icon: Layers,
  },
  {
    id: 2,
    title: t('whyChooseUs.reason2.title'),
    description: t('whyChooseUs.reason2.desc'),
    points: [
      t('whyChooseUs.reason2.p1'),
      t('whyChooseUs.reason2.p2'),
      t('whyChooseUs.reason2.p3')
    ],
    icon: Clock,
  },
  {
    id: 3,
    title: t('whyChooseUs.reason3.title'),
    description: t('whyChooseUs.reason3.desc'),
    points: [
      t('whyChooseUs.reason3.p1'),
      t('whyChooseUs.reason3.p2'),
      t('whyChooseUs.reason3.p3')
    ],
    icon: ShieldCheck,
  }
];

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const ReasonToChooseUs = () => {
  const { t, i18n } = useTranslation();
  const isEng = i18n.language && i18n.language.startsWith('en');
  const bgSlides = [def1, def2, def3, def4, def5, def6, def7, def8, def9];
  const [bgSlide, setBgSlide] = useState(6); // Start at def7
  const [activeReason, setActiveReason] = useState(null);
  const handleClick = useCallback((i) => setActiveReason(prev => prev === i ? null : i), []);

  const reasons = getReasons(t);

  useEffect(() => {
    const interval = setInterval(() => {
      setBgSlide((prev) => (prev + 1) % bgSlides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [bgSlides.length]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { type: "spring", stiffness: 100, damping: 15 }
    },
    hover: { 
      y: -10,
      scale: 1.02,
      transition: { type: "spring", stiffness: 400, damping: 10 }
    }
  };

  return (
    <section id="why-choose-us" className="section-padding bg-gray-50 dark:bg-auxis-charcoal relative overflow-hidden transition-colors duration-500">
      {/* Background Image Carousel */}
      <div className="absolute inset-0">
        <AnimatePresence mode="wait">
          <motion.img 
            key={bgSlide}
            src={bgSlides[bgSlide]} 
            alt={`AUXIS Background ${bgSlide + 1}`}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-auxis-charcoal/90 via-auxis-charcoal/70 to-auxis-charcoal/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-auxis-charcoal via-transparent to-auxis-charcoal/30" />
      </div>

      <div className="container-tight relative z-10">
        {/* Header */}
        <div className="text-center mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="inline-flex items-center gap-2.5 text-caption uppercase font-bold tracking-[0.15em] text-auxis-gold mb-5">
              <span className="w-2 h-2 rounded-full bg-auxis-gold" />
              {t('whyChooseUs.tag')}
            </span>
            <h2 className="text-display text-white text-center mb-5">{t('whyChooseUs.title')}</h2>
            <div className="gold-line mx-auto mb-6" />
            <p className="text-body-lg text-gray-300 max-w-2xl mx-auto">
              {t('whyChooseUs.subtitle')}
            </p>
          </motion.div>
        </div>

        {/* Reason Cards */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-16"
        >
          {reasons.map((reason, index) => {
            const isActive = activeReason === index;
            
            return (
              <motion.div
                key={reason.id}
                variants={cardVariants}
                whileHover="hover"
                className={`bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden transition-all duration-300 ${
                  isActive ? 'ring-2 ring-auxis-gold shadow-xl bg-white/20' : 'hover:shadow-xl hover:bg-white/15'
                }`}
              >
                <div className="p-8">
                  {/* Icon */}
                  <div className="w-16 h-16 rounded-2xl bg-auxis-gold/20 flex items-center justify-center mb-6 transform transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3">
                    <reason.icon className="w-8 h-8 text-auxis-gold" />
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">{reason.title}</h3>
                  <p className="text-gray-300 mb-6">{reason.description}</p>

                  {/* Toggle */}
                  <button
                    onClick={() => handleClick(index)}
                    className="flex items-center text-sm font-semibold text-auxis-gold hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors group"
                  >
                    {isActive ? t('whyChooseUs.seeLess') : t('whyChooseUs.learnMore')}
                    <motion.div
                      animate={{ rotate: isActive ? 90 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </motion.div>
                  </button>

                  {/* Expandable details */}
                  <AnimatePresence>
                    {isActive && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <ul className="mt-6 space-y-3 pt-6 border-t border-white/20">
                          {reason.points.map((point, idx) => (
                            <motion.li 
                              key={idx}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.1 }}
                              className="flex items-start text-sm text-gray-200"
                            >
                              <CheckCircle2 className="w-5 h-5 mr-3 flex-shrink-0 text-auxis-gold" />
                              <span>{point}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.6 }}
          className="text-center"
        >
          <a
            href="https://wa.me/261349103131"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-auxis-gold text-auxis-charcoal rounded-full font-bold text-sm shadow-lg hover:shadow-xl hover:bg-white transition-all duration-300 hover:-translate-y-1"
          >
            <WhatsAppIcon />
            {t('whyChooseUs.contact')}
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default ReasonToChooseUs;
